import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// LINE icon component
const LineIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

export const Footer = () => {
  const { t, language } = useLanguage();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    fetchInfo();
  }, []);

  const fetchInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/settings`);
      setInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const quickLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/courses', label: t('nav.courses') },
    { href: '/news', label: t('nav.news') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
    { href: '/apply', label: t('nav.apply') },
    { href: '/admin', label: t('footer.adminLogin') },
  ];

  const legalLinks = [
    { href: '/recruitment', label: t('footer.recruitment') },
    { href: '/membership', label: t('footer.membership') },
    { href: '/reports', label: t('footer.reports') },
    { href: '/privacy', label: t('footer.privacy') },
    { href: '/commercial-law', label: t('footer.commercialLaw') },
    { href: '/sustainability', label: t('footer.sustainability') },
  ];

  // Parse address to separate postal code and address
  const parseAddress = (address) => {
    if (!address) return { postalCode: '', addressLine: '' };
    const match = address.match(/^(〒[\d-]+)\s*(.*)$/);
    if (match) {
      return { postalCode: match[1], addressLine: match[2] };
    }
    return { postalCode: '', addressLine: address };
  };

  const addressDisplay = language === 'ja' ? info?.address : t('footer.addressRomaji');
  const { postalCode, addressLine } = parseAddress(addressDisplay || '');

  return (
    <footer className="bg-[#111827] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
          {/* Logo & Description */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/about" className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity">
              <img 
                src={info?.logoImage || "https://customer-assets.emergentagent.com/job_jwsea-edu/artifacts/885p674v_image.png"} 
                alt="JWSEA Logo" 
                className="h-8 w-auto bg-white rounded p-1"
              />
              <div>
                <span className="font-bold text-white text-[13px] hover:text-[#00A651] transition-colors">JWSEA Foundation</span>
                <span className="block text-[11px] text-gray-400">
                  {language === 'ja' ? (info?.organizationName || t('footer.orgName')) : (info?.organizationNameEn || t('footer.orgName'))}
                </span>
              </div>
            </Link>
            <Link to="/about" className="block text-gray-400 leading-relaxed text-[11px] hover:text-gray-300 transition-colors">
              {t('footer.description')}
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-white">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-[#00A651] transition-colors"
                    data-testid={`footer-link-${link.href.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Info Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-white">{t('footer.info')}</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-gray-400 hover:text-[#00A651] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-white">{t('footer.contactInfo')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#00A651] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {postalCode && <>{postalCode}<br /></>}
                  {addressLine || t('footer.addressNotSet')}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#00A651] flex-shrink-0" />
                <a href={`tel:${info?.phone || ''}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {info?.phone || t('footer.phoneNotSet')}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#00A651] flex-shrink-0" />
                <a href={`mailto:${info?.email || ''}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {info?.email || t('footer.emailNotSet')}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#00A651] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm whitespace-pre-line">
                  {language === 'ja' ? (info?.businessHours || t('footer.hoursNotSet')) : t('footer.businessHoursDefault')}
                </span>
              </li>
              {info?.whatsapp && (
                <li className="flex items-center gap-3">
                  <MessageCircle className="h-5 w-5 text-[#25D366] flex-shrink-0" />
                  <a 
                    href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#25D366] transition-colors text-sm"
                  >
                    WhatsApp: {info.whatsapp}
                  </a>
                </li>
              )}
              {info?.lineId && (
                <li className="flex items-center gap-3">
                  <LineIcon className="h-5 w-5 text-[#00B900] flex-shrink-0" />
                  <a 
                    href={`https://line.me/ti/p/${info.lineId.startsWith('@') ? info.lineId : '@' + info.lineId}`}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-[#00B900] transition-colors text-sm"
                  >
                    LINE: {info.lineId}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};
